// Placement query/mutation hooks (placements.rs).
//   GET/POST /v1/surveys/{surveyId}/placements
//   PATCH/DELETE /v1/placements/{id}, POST /v1/placements/{id}/token/rotate
//
// Placements have no detail cache (placementKeys carries only `list`, unlike
// surveys/screens) — there is no standalone placement page, only rows in a
// survey's placements list — so every mutation invalidates that list. Adding
// or deleting one also changes the surveys list's Placements count and its
// row Delete rule, so those two refresh the surveys search as well (hence
// the workspaceId). PATCH/DELETE/rotate are scoped by placementId only on the wire, so callers
// pass surveyId alongside for cache invalidation (same shape as templates.ts).

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "./http";
import { placementKeys, surveyKeys } from "./keys";
import type { Placement, PlacementMode, PlacementRow } from "./types";

export interface CreatePlacement {
    name: string;
    mode?: PlacementMode;
    attractEnabled?: boolean;
    attractText?: string | null;
    resetSeconds?: number;
    thanksSeconds?: number;
    revisit?: "show_submitted" | "hide" | "allow_again";
    locationRef?: string | null;
    inviteTtlDays?: number;
}

/** Absent leaves the column, null clears it, a value sets it. Only
 * `attractText` is nullable (placements.rs PatchPlacement). */
export interface PatchPlacement {
    name?: string;
    active?: boolean;
    mode?: PlacementMode;
    attractEnabled?: boolean;
    attractText?: string | null;
    resetSeconds?: number;
    thanksSeconds?: number;
    revisit?: "show_submitted" | "hide" | "allow_again";
    locationRef?: string | null;
    inviteTtlDays?: number;
}

export function usePlacements(surveyId: string) {
    return useQuery({
        queryKey: placementKeys.list(surveyId),
        queryFn: () => http.get<PlacementRow[]>(`/v1/surveys/${surveyId}/placements`),
        enabled: Boolean(surveyId),
    });
}

export function useCreatePlacement(workspaceId: string, surveyId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: CreatePlacement) =>
            http.post<Placement>(`/v1/surveys/${surveyId}/placements`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: placementKeys.list(surveyId) });
            queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) });
        },
    });
}

export function usePatchPlacement(surveyId: string, placementId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: (body: PatchPlacement) => http.patch<Placement>(`/v1/placements/${placementId}`, { json: body }),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: placementKeys.list(surveyId) });
        },
    });
}

export function useDeletePlacement(workspaceId: string, surveyId: string, placementId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.delete<void>(`/v1/placements/${placementId}`),
        // On a refusal too (a response landed since the list loaded), and
        // awaited, so the caller reads the fresh count once this settles.
        onSettled: () =>
            Promise.all([
                queryClient.invalidateQueries({ queryKey: placementKeys.list(surveyId) }),
                queryClient.invalidateQueries({ queryKey: surveyKeys.searchRoot(workspaceId) }),
            ]),
    });
}

export function useRotateToken(surveyId: string, placementId: string) {
    const queryClient = useQueryClient();
    return useMutation({
        mutationFn: () => http.post<Placement>(`/v1/placements/${placementId}/token/rotate`),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: placementKeys.list(surveyId) });
        },
    });
}
